import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Table, Modal, Form } from 'react-bootstrap'
import { productionAPI } from '../api/productionAPI'
import { recipeAPI } from '../api/recipeAPI'
import { locationAPI } from '../api/locationAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import PickInventoryModal from '../components/PickInventoryModal'
import ConfirmationModal from '../components/ConfirmationModal'

function ProductionDetailScreen() {
    const { id } = useParams()
    const navigate = useNavigate()

    const [productionLot, setProductionLot] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    // Add inventory modal state
    const [showAddInventoryModal, setShowAddInventoryModal] = useState(false)
    const [selectedIngredientForAdd, setSelectedIngredientForAdd] = useState(null)
    const [addingInventory, setAddingInventory] = useState(false)

    // Return inventory modal state
    const [showReturnModal, setShowReturnModal] = useState(false)
    const [selectedAllocationForReturn, setSelectedAllocationForReturn] = useState(null)
    const [returnQuantity, setReturnQuantity] = useState('')
    const [returnLocationId, setReturnLocationId] = useState('')
    const [returningInventory, setReturningInventory] = useState(false)
    const [warehouses, setWarehouses] = useState([])
    const [locations, setLocations] = useState([])
    const [selectedWarehouse, setSelectedWarehouse] = useState('')

    // Cancel modal state
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancelling, setCancelling] = useState(false)

    useEffect(() => {
        fetchProductionLot()
        fetchWarehouses()
    }, [id])

    useEffect(() => {
        if (selectedWarehouse) {
            fetchLocations(selectedWarehouse)
        } else {
            setLocations([])
        }
    }, [selectedWarehouse])

    const fetchProductionLot = async () => {
        try {
            setLoading(true)
            const data = await productionAPI.getById(id)
            setProductionLot(data)
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to load production LOT')
        } finally {
            setLoading(false)
        }
    }

    const fetchWarehouses = async () => {
        try {
            const data = await warehouseAPI.getAll()
            const activeWarehouses = Array.isArray(data)
                ? data.filter(w => w.is_active)
                : (data.results || []).filter(w => w.is_active)
            setWarehouses(activeWarehouses)
        } catch (error) {
            console.error('Error fetching warehouses:', error)
        }
    }

    const fetchLocations = async (warehouseId) => {
        try {
            const data = await locationAPI.getAll(warehouseId)
            const activeLocations = Array.isArray(data)
                ? data.filter(l => l.is_active)
                : (data.results || []).filter(l => l.is_active)
            setLocations(activeLocations)
        } catch (error) {
            console.error('Error fetching locations:', error)
            setLocations([])
        }
    }

    const getStatusBadge = (status) => {
        switch (status) {
            case 'in_progress':
                return <Badge bg="primary">In Progress</Badge>
            case 'completed':
                return <Badge bg="success">Completed</Badge>
            case 'cancelled':
                return <Badge bg="secondary">Cancelled</Badge>
            default:
                return <Badge bg="light" text="dark">{status}</Badge>
        }
    }

    const formatDateTime = (dateString) => {
        if (!dateString) return '-'
        const date = new Date(dateString)
        return date.toLocaleString()
    }

    // Handle opening add inventory modal
    const openAddInventoryModal = (lotIngredient) => {
        setSelectedIngredientForAdd(lotIngredient)
        setShowAddInventoryModal(true)
    }

    // Handle add inventory confirmation
    const handleAddInventoryConfirm = async (allocations) => {
        if (!selectedIngredientForAdd) return

        setAddingInventory(true)
        setError(null)

        try {
            // Convert allocations to the format expected by the API
            const apiAllocations = Object.entries(allocations).map(([inventoryItemId, quantity]) => ({
                ingredient_id: selectedIngredientForAdd.recipe_ingredient,
                inventory_item_id: parseInt(inventoryItemId),
                quantity: parseFloat(quantity)
            })).filter(a => a.quantity > 0)

            if (apiAllocations.length === 0) {
                setError('No valid allocations to add')
                return
            }

            await productionAPI.addInventory(id, apiAllocations)
            setSuccessMessage('Inventory added successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to add inventory')
        } finally {
            setAddingInventory(false)
            setShowAddInventoryModal(false)
            setSelectedIngredientForAdd(null)
        }
    }

    // Handle opening return modal
    const openReturnModal = (allocation) => {
        setSelectedAllocationForReturn(allocation)
        setReturnQuantity(allocation.quantity_allocated.toString())
        setReturnLocationId('')
        setSelectedWarehouse('')
        setShowReturnModal(true)
    }

    // Handle return inventory
    const handleReturnInventory = async () => {
        if (!selectedAllocationForReturn || !returnLocationId || !returnQuantity) {
            setError('Please fill in all return fields')
            return
        }

        const qty = parseFloat(returnQuantity)
        if (isNaN(qty) || qty <= 0 || qty > selectedAllocationForReturn.quantity_allocated) {
            setError('Invalid return quantity')
            return
        }

        setReturningInventory(true)
        setError(null)

        try {
            await productionAPI.returnInventory(id, [{
                production_lot_inventory_item_id: selectedAllocationForReturn._id,
                quantity: qty,
                return_location_id: parseInt(returnLocationId)
            }])
            setSuccessMessage('Inventory returned successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to return inventory')
        } finally {
            setReturningInventory(false)
            setShowReturnModal(false)
            setSelectedAllocationForReturn(null)
        }
    }

    // Handle cancel production
    const handleCancelProduction = async () => {
        setCancelling(true)
        setError(null)

        try {
            await productionAPI.cancel(id)
            setSuccessMessage('Production LOT cancelled')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to cancel production LOT')
        } finally {
            setCancelling(false)
            setShowCancelModal(false)
        }
    }

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (!productionLot) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error || 'Production lot not found'}</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/productions')}>
                    Back to List
                </Button>
            </Container>
        )
    }

    const isInProgress = productionLot.status === 'in_progress'

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>Production LOT: {productionLot.lot_number}</h1>
                </Col>
                <Col xs="auto">
                    {getStatusBadge(productionLot.status)}
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            )}

            {/* Production LOT Details */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Details</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col xs="auto">
                            {productionLot.output_stock_item_image ? (
                                <img
                                    src={productionLot.output_stock_item_image}
                                    alt={productionLot.output_stock_item_name}
                                    style={{
                                        width: '140px',
                                        height: '140px',
                                        objectFit: 'contain'
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '140px',
                                        height: '140px',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    No Image
                                </div>
                            )}
                        </Col>
                        <Col md={5}>
                            <p><strong>Recipe:</strong>{' '}
                                <Link to={`/recipe/${productionLot.recipe}`}>
                                    {productionLot.recipe_name}
                                </Link>
                            </p>
                            <p><strong>Output Product:</strong> {productionLot.output_stock_item_name}</p>
                            <p><strong>Target Quantity:</strong> {productionLot.target_quantity}</p>
                            <p><strong>Production Location:</strong> {productionLot.production_location_name}</p>
                        </Col>
                        <Col md={6}>
                            <p><strong>Started At:</strong> {formatDateTime(productionLot.started_at)}</p>
                            <p><strong>Started By:</strong> {productionLot.started_by_name}</p>
                            {productionLot.completed_at && (
                                <>
                                    <p><strong>Completed At:</strong> {formatDateTime(productionLot.completed_at)}</p>
                                    <p><strong>Completed By:</strong> {productionLot.completed_by_name}</p>
                                </>
                            )}
                            {productionLot.actual_quantity_produced && (
                                <p><strong>Actual Quantity Produced:</strong> {productionLot.actual_quantity_produced}</p>
                            )}
                            {productionLot.total_cost && (
                                <p><strong>Total Cost:</strong> ${parseFloat(productionLot.total_cost).toFixed(2)}</p>
                            )}
                            {productionLot.unit_cost && (
                                <p><strong>Unit Cost:</strong> ${parseFloat(productionLot.unit_cost).toFixed(2)}</p>
                            )}
                        </Col>
                    </Row>
                    {productionLot.notes && (
                        <Row>
                            <Col>
                                <p><strong>Notes:</strong> {productionLot.notes}</p>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Ingredients */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Ingredients</h5>
                </Card.Header>
                <Card.Body>
                    {productionLot.lot_ingredients && productionLot.lot_ingredients.length > 0 ? (
                        productionLot.lot_ingredients.map((lotIngredient) => (
                            <Card key={lotIngredient._id} className="mb-3">
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <span>
                                        <strong>{lotIngredient.ingredient_name}</strong>
                                        {' '}
                                        <Badge bg="info" className="ms-2">
                                            Required: {lotIngredient.required_quantity}
                                        </Badge>
                                        {' '}
                                        <Badge bg={
                                            parseFloat(lotIngredient.picked_quantity) >= parseFloat(lotIngredient.required_quantity)
                                                ? 'success' : 'warning'
                                        }>
                                            Picked: {lotIngredient.picked_quantity}
                                        </Badge>
                                        {lotIngredient.used_quantity !== null && (
                                            <Badge bg="secondary" className="ms-2">
                                                Used: {lotIngredient.used_quantity}
                                            </Badge>
                                        )}
                                    </span>
                                    {isInProgress && (
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => openAddInventoryModal(lotIngredient)}
                                        >
                                            Add More
                                        </Button>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    {lotIngredient.inventory_allocations && lotIngredient.inventory_allocations.length > 0 ? (
                                        <Table size="sm" striped>
                                            <thead>
                                                <tr>
                                                    <th>Inventory Item</th>
                                                    <th>LOT</th>
                                                    <th>Original Location</th>
                                                    <th>Allocated</th>
                                                    {lotIngredient.used_quantity !== null && (
                                                        <>
                                                            <th>Used</th>
                                                            <th>Returned</th>
                                                        </>
                                                    )}
                                                    {isInProgress && <th className="text-end">Actions</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lotIngredient.inventory_allocations.map((allocation) => (
                                                    <tr key={allocation._id}>
                                                        <td>{allocation.inventory_item_data?.stock_item?.description || '-'}</td>
                                                        <td>{allocation.inventory_item_data?.lot || '-'}</td>
                                                        <td>{allocation.original_location_name || '-'}</td>
                                                        <td>{allocation.quantity_allocated}</td>
                                                        {lotIngredient.used_quantity !== null && (
                                                            <>
                                                                <td>{allocation.quantity_used || 0}</td>
                                                                <td>{allocation.quantity_returned || 0}</td>
                                                            </>
                                                        )}
                                                        {isInProgress && (
                                                            <td>
                                                                <div className="d-flex gap-1 justify-content-end">
                                                                    <Button
                                                                        variant="outline-warning"
                                                                        size="sm"
                                                                        onClick={() => openReturnModal(allocation)}
                                                                    >
                                                                        Return
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => {/* TODO: implement dispose */}}
                                                                    >
                                                                        Dispose
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <p className="text-muted mb-0">No inventory allocated</p>
                                    )}
                                </Card.Body>
                            </Card>
                        ))
                    ) : (
                        <p className="text-muted">No ingredients in this production LOT</p>
                    )}
                </Card.Body>
            </Card>

            {/* Action Buttons */}
            <div className="d-flex gap-2">
                {isInProgress && (
                    <>
                        <Button
                            variant="success"
                            onClick={() => navigate(`/production/${id}/finalize`)}
                        >
                            Finalize Production
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => setShowCancelModal(true)}
                        >
                            Cancel Production
                        </Button>
                    </>
                )}
                <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/productions')}
                >
                    Back to List
                </Button>
            </div>

            {/* Add Inventory Modal */}
            {selectedIngredientForAdd && (
                <PickInventoryModal
                    show={showAddInventoryModal}
                    onHide={() => {
                        setShowAddInventoryModal(false)
                        setSelectedIngredientForAdd(null)
                    }}
                    ingredient={{
                        _id: selectedIngredientForAdd.recipe_ingredient,
                        ingredient_name: selectedIngredientForAdd.ingredient_name,
                        ...selectedIngredientForAdd.recipe_ingredient_data
                    }}
                    requiredQuantity={0} // No specific requirement for additional picks
                    recipeId={productionLot.recipe}
                    onConfirm={handleAddInventoryConfirm}
                    existingAllocations={null}
                />
            )}

            {/* Return Inventory Modal */}
            <Modal show={showReturnModal} onHide={() => setShowReturnModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Return Inventory</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAllocationForReturn && (
                        <>
                            <p>
                                <strong>Item:</strong> {selectedAllocationForReturn.inventory_item_data?.stock_item?.description}
                            </p>
                            <p>
                                <strong>Available to Return:</strong> {selectedAllocationForReturn.quantity_allocated}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Quantity to Return</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.0001"
                                    min="0.0001"
                                    max={selectedAllocationForReturn.quantity_allocated}
                                    value={returnQuantity}
                                    onChange={(e) => setReturnQuantity(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Return to Warehouse</Form.Label>
                                <Form.Select
                                    value={selectedWarehouse}
                                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                                >
                                    <option value="">Select Warehouse...</option>
                                    {warehouses.map(wh => (
                                        <option key={wh._id} value={wh._id}>
                                            {wh.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Return to Location</Form.Label>
                                <Form.Select
                                    value={returnLocationId}
                                    onChange={(e) => setReturnLocationId(e.target.value)}
                                    disabled={!selectedWarehouse}
                                >
                                    <option value="">Select Location...</option>
                                    {locations.map(loc => (
                                        <option key={loc._id} value={loc._id}>
                                            {loc.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReturnModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleReturnInventory}
                        disabled={returningInventory || !returnLocationId || !returnQuantity}
                    >
                        {returningInventory ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Returning...
                            </>
                        ) : (
                            'Return Inventory'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Cancel Confirmation Modal */}
            <ConfirmationModal
                show={showCancelModal}
                onHide={() => setShowCancelModal(false)}
                onConfirm={handleCancelProduction}
                title="Cancel Production"
                message={`Are you sure you want to cancel this production LOT? Inventory will remain at the production location (${productionLot.production_location_name}).`}
                confirmButtonText={cancelling ? 'Cancelling...' : 'Cancel Production'}
                confirmButtonVariant="danger"
            />
        </Container>
    )
}

export default ProductionDetailScreen
